import React from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, Lock, Trash2, Shield, Zap, BookOpen, HardDrive } from 'lucide-react';
import { getStoreUrl } from '../utils/storeUrls';

/* ── CSS-art panels ─────────────────────────────────────────── */

const LanguagePillsArt = () => {
  const { t } = useTranslation();
  const languageList = [
    { name: 'English', hi: true },
    { name: 'Español', hi: false },
    { name: 'Français', hi: false },
    { name: 'Deutsch', hi: false },
    { name: 'Português', hi: true },
    { name: 'Italiano', hi: false },
    { name: 'Nederlands', hi: false },
    { name: 'Svenska', hi: false },
    { name: 'Norsk', hi: false },
    { name: 'Polski', hi: false },
    { name: '日本語', hi: true },
    { name: '한국어', hi: false },
    { name: '中文', hi: false },
    { name: 'हिन्दी', hi: false },
    { name: 'العربية', hi: false },
    { name: 'Tiếng Việt', hi: false },
    { name: t('featuresPage.art.languages.more', { defaultValue: '+14 more' }), hi: false },
  ];
  return (
  <div
    style={{
      background: 'var(--v2-card)',
      border: '1px solid var(--v2-line)',
      borderRadius: '20px',
      padding: '32px 28px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      alignContent: 'flex-start',
    }}
  >
    {languageList.map((lang, i) => (
      <span
        key={i}
        style={{
          padding: '7px 16px',
          borderRadius: '999px',
          fontSize: '13px',
          fontWeight: 600,
          background: lang.hi ? 'var(--v2-primary)' : 'var(--v2-bg-subtle)',
          color: lang.hi ? '#fff' : 'var(--v2-ink-muted)',
          border: lang.hi ? '1px solid var(--v2-primary)' : '1px solid var(--v2-line)',
        }}
      >
        {lang.name}
      </span>
    ))}
    <div
      style={{
        width: '100%',
        marginTop: '16px',
        padding: '12px 16px',
        borderRadius: '12px',
        background: 'var(--v2-primary-light)',
        border: '1px solid var(--v2-primary)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '13px',
        color: 'var(--v2-primary)',
        fontWeight: 600,
      }}
    >
      <span style={{ fontSize: '18px' }}>🌐</span>
      {t('featuresPage.art.languages.footer', { defaultValue: '30 languages supported · Whisper AI' })}
    </div>
  </div>
  );
};

const SummaryArt = () => {
  const { t } = useTranslation();
  const raw = t('featuresPage.art.summary.bullets', { returnObjects: true });
  const summaryBullets: string[] = Array.isArray(raw) ? (raw as string[]) : [
    'Pull API deadline forward; demo at All-Hands on Apr 22',
    'Ravi owns beta retention numbers by Friday EOD',
    'Cut free tier to 15 min/day; raise Pro to 10h',
    'Ship bullet/paragraph toggle in next release',
  ];
  return (
  <div
    style={{
      background: 'var(--v2-card)',
      border: '1px solid var(--v2-line)',
      borderRadius: '20px',
      padding: '24px',
      boxShadow: 'var(--v2-shadow-soft)',
    }}
  >
    <div
      style={{
        fontFamily: '"JetBrains Mono", ui-monospace, monospace',
        fontSize: '11px',
        fontWeight: 500,
        color: 'var(--v2-ink-muted)',
        letterSpacing: '0.04em',
        marginBottom: '12px',
      }}
    >
      {t('featuresPage.art.summary.header', { defaultValue: 'Q2 ROADMAP SYNC · 58 MIN · APR 18' })}
    </div>
    {summaryBullets.map((b, i) => (
      <div
        key={i}
        style={{
          background: 'var(--v2-bg-subtle)',
          borderRadius: '10px',
          padding: '10px 12px',
          fontSize: '13.5px',
          borderLeft: '3px solid var(--v2-primary)',
          marginBottom: '8px',
          color: 'var(--v2-ink-2)',
          lineHeight: 1.5,
        }}
      >
        {b}
      </div>
    ))}
  </div>
  );
};

const TranscriptArt = () => {
  const { t } = useTranslation();
  type TranscriptLine = { speaker: string; text: string; hi: boolean };
  const rawLines = t('featuresPage.art.transcript.lines', { returnObjects: true });
  const transcriptLines: TranscriptLine[] = Array.isArray(rawLines) ? (rawLines as TranscriptLine[]) : [
    { speaker: 'Alex', text: 'So the main concern with the Q2 roadmap is timeline.', hi: false },
    { speaker: 'Mia', text: 'Right, we need to pull the API deadline forward.', hi: true },
    { speaker: 'Alex', text: 'The All-Hands demo is April 22nd — firm date.', hi: false },
    { speaker: 'Ravi', text: 'I can own beta retention numbers by Friday.', hi: true },
    { speaker: 'Mia', text: "That works. Let's also review the Pro pricing.", hi: false },
  ];
  return (
  <div
    style={{
      background: 'var(--v2-card)',
      border: '1px solid var(--v2-line)',
      borderRadius: '20px',
      padding: '24px',
      boxShadow: 'var(--v2-shadow-soft)',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }} />
      <div
        style={{
          fontFamily: '"JetBrains Mono", ui-monospace, monospace',
          fontSize: '11px',
          color: 'var(--v2-ink-muted)',
          letterSpacing: '0.04em',
          fontWeight: 600,
        }}
      >
        {t('featuresPage.art.transcript.status', { defaultValue: 'LIVE · TRANSCRIBING' })}
      </div>
    </div>
    {transcriptLines.map((line, i) => (
      <div
        key={i}
        style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '10px',
          padding: '8px 10px',
          borderRadius: '10px',
          background: line.hi ? 'var(--v2-primary-light)' : 'var(--v2-bg-subtle)',
          border: line.hi ? '1px solid var(--v2-primary)' : '1px solid transparent',
        }}
      >
        <span
          style={{
            fontSize: '11px',
            fontWeight: 700,
            color: line.hi ? 'var(--v2-primary)' : 'var(--v2-ink-muted)',
            minWidth: '36px',
            paddingTop: '1px',
            flexShrink: 0,
          }}
        >
          {line.speaker}
        </span>
        <span style={{ fontSize: '13px', color: 'var(--v2-ink)', lineHeight: 1.5 }}>
          {line.text}
        </span>
      </div>
    ))}
    <div
      style={{
        marginTop: '12px',
        padding: '8px 12px',
        borderRadius: '8px',
        background: 'var(--v2-bg-subtle)',
        fontSize: '12px',
        color: 'var(--v2-ink-muted)',
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <span>{t('featuresPage.art.transcript.speakersDetected', { defaultValue: '3 speakers detected' })}</span>
      <span style={{ color: 'var(--v2-primary)', fontWeight: 600 }}>{t('featuresPage.art.transcript.accuracy', { defaultValue: '99.2% accuracy' })}</span>
    </div>
  </div>
  );
};

/* ── Bento card ─────────────────────────────────────────────── */

interface BentoCardProps {
  label: string;
  title: string;
  description: string;
  bullets: string[];
  art: React.ReactNode;
  reversed?: boolean;
}

const BentoCard = ({ label, title, description, bullets, art, reversed = false }: BentoCardProps) => (
  <div
    style={{
      background: 'var(--v2-card)',
      border: '1px solid var(--v2-line)',
      borderRadius: '24px',
      padding: 'clamp(28px, 4vw, 48px)',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: '40px',
      alignItems: 'center',
      direction: reversed ? 'rtl' : 'ltr',
    }}
  >
    <div style={{ direction: 'ltr' }}>
      <div
        style={{
          color: 'var(--v2-primary)',
          fontWeight: 700,
          fontSize: '11px',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          marginBottom: '12px',
        }}
      >
        {label}
      </div>
      <h2
        style={{
          fontSize: 'clamp(22px, 2.8vw, 34px)',
          fontWeight: 800,
          color: 'var(--v2-ink)',
          marginBottom: '12px',
          lineHeight: 1.2,
        }}
      >
        {title}
      </h2>
      <p
        style={{
          fontSize: '15px',
          color: 'var(--v2-ink-muted)',
          lineHeight: 1.65,
          marginBottom: '20px',
        }}
      >
        {description}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {bullets.map((b, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'var(--v2-primary)',
                marginTop: '7px',
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: '14px', color: 'var(--v2-ink-muted)' }}>{b}</span>
          </div>
        ))}
      </div>
    </div>
    <div style={{ direction: 'ltr' }}>{art}</div>
  </div>
);

/* ── Privacy rows ───────────────────────────────────────────── */

const privacyRowDefs = [
  { icon: <Ban size={18} />, color: '#ef4444', bg: 'rgba(239,68,68,0.1)', key: 'onDevice' },
  { icon: <Lock size={18} />, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', key: 'encrypted' },
  { icon: <Trash2 size={18} />, color: '#6b7280', bg: 'rgba(107,114,128,0.1)', key: 'delete' },
  { icon: <Shield size={18} />, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', key: 'noSharing' },
];

/* ── Secondary features ─────────────────────────────────────── */

const secondaryDefs = [
  { key: 'realtime', icon: <Zap size={22} />, color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)' },
  { key: 'vocabulary', icon: <BookOpen size={22} />, color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  { key: 'secure', icon: <HardDrive size={22} />, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
];

/* ── Main component ─────────────────────────────────────────── */

const FeaturesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const storeUrl = getStoreUrl(i18n.language);

  const getBullets = (key: string, fallback: string[]): string[] => {
    const val = t(key, { returnObjects: true });
    return Array.isArray(val) ? (val as string[]) : fallback;
  };

  const transcriptionBullets = getBullets('featuresPage.bento.transcription.bullets', [
    'Record live or import audio files',
    '99%+ accuracy across accents',
    'Speaker identification',
  ]);
  const summariesBullets = getBullets('featuresPage.bento.summaries.bullets', [
    'Meeting recaps with action items',
    'Key insights extracted automatically',
    'Custom summary templates',
  ]);
  const languagesBullets = getBullets('featuresPage.bento.languages.bullets', [
    'All major world languages',
    'Regional dialects supported',
    'Live translation between languages',
  ]);

  return (
    <main style={{ background: 'var(--v2-bg)', color: 'var(--v2-ink)', minHeight: '100vh' }}>

      {/* ── Hero ── */}
      <section
        style={{
          maxWidth: '720px',
          margin: '0 auto',
          padding: 'clamp(64px, 8vw, 96px) 24px clamp(40px, 5vw, 64px)',
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            fontSize: 'clamp(2rem, 5vw, 3.2rem)',
            fontWeight: 800,
            lineHeight: 1.1,
            marginBottom: '16px',
            color: 'var(--v2-ink)',
          }}
        >
          {t('featuresPage.hero.headline', 'Every word, captured perfectly')}
        </h1>
        <p
          style={{
            fontSize: 'clamp(1rem, 2vw, 1.2rem)',
            color: 'var(--v2-ink-muted)',
            lineHeight: 1.6,
            maxWidth: '560px',
            margin: '0 auto',
          }}
        >
          {t(
            'featuresPage.hero.subheadline',
            'AI transcription, smart summaries, and 30+ languages — all in one app.'
          )}
        </p>
      </section>

      {/* ── Bento Feature Grid ── */}
      <section style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px 80px' }}>
        <h2
          style={{
            fontSize: 'clamp(1rem, 1.5vw, 1.1rem)',
            fontWeight: 700,
            color: 'var(--v2-ink-muted)',
            textAlign: 'center',
            marginBottom: '40px',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {t('featuresPage.bento.sectionTitle', 'Three features that change everything')}
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* AI Transcription */}
          <BentoCard
            label={t('featuresPage.bento.transcription.label', 'AI TRANSCRIPTION')}
            title={t('featuresPage.bento.transcription.title', 'Industry-leading accuracy')}
            description={t(
              'featuresPage.bento.transcription.description',
              'Powered by OpenAI Whisper, Notely converts speech to text with 99%+ accuracy — live or from uploaded files.'
            )}
            bullets={transcriptionBullets}
            art={<TranscriptArt />}
          />

          {/* Smart Summaries */}
          <BentoCard
            label={t('featuresPage.bento.summaries.label', 'SMART SUMMARIES')}
            title={t('featuresPage.bento.summaries.title', 'AI that reads between the lines')}
            description={t(
              'featuresPage.bento.summaries.description',
              'Get instant meeting recaps, extracted action items, and key insights the moment your recording ends.'
            )}
            bullets={summariesBullets}
            art={<SummaryArt />}
            reversed
          />

          {/* Multi-language */}
          <BentoCard
            label={t('featuresPage.bento.languages.label', '30+ LANGUAGES')}
            title={t('featuresPage.bento.languages.title', 'Speak any language, get perfect notes')}
            description={t(
              'featuresPage.bento.languages.description',
              'Notely supports over 30 languages and regional accents with the same industry-leading accuracy.'
            )}
            bullets={languagesBullets}
            art={<LanguagePillsArt />}
          />
        </div>
      </section>

      {/* ── Secondary features strip ── */}
      <section
        style={{
          background: 'var(--v2-bg-subtle)',
          borderTop: '1px solid var(--v2-line)',
          borderBottom: '1px solid var(--v2-line)',
          padding: 'clamp(48px, 6vw, 72px) 24px',
        }}
      >
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2
            style={{
              fontSize: 'clamp(1.2rem, 2vw, 1.5rem)',
              fontWeight: 800,
              color: 'var(--v2-ink)',
              textAlign: 'center',
              marginBottom: '40px',
            }}
          >
            {t('featuresPage.secondary.sectionTitle', "More features you'll love")}
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '20px',
            }}
          >
            {secondaryDefs.map(({ key, icon, color, bg }) => (
              <div
                key={key}
                style={{
                  background: 'var(--v2-card)',
                  border: '1px solid var(--v2-line)',
                  borderRadius: '16px',
                  padding: '28px 24px',
                }}
              >
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: bg,
                    color: color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px',
                  }}
                >
                  {icon}
                </div>
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: 'var(--v2-ink)',
                    marginBottom: '8px',
                  }}
                >
                  {t(
                    `featuresPage.secondary.${key}.title`,
                    key === 'realtime'
                      ? 'Real-time Processing'
                      : key === 'vocabulary'
                      ? 'Custom Vocabulary'
                      : 'Secure Storage'
                  )}
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--v2-ink-muted)', lineHeight: 1.6, margin: 0 }}>
                  {t(
                    `featuresPage.secondary.${key}.description`,
                    key === 'realtime'
                      ? 'See transcriptions appear as you speak, with no waiting.'
                      : key === 'vocabulary'
                      ? 'Train the AI on your industry-specific terms and jargon.'
                      : 'Enterprise-grade encryption keeps all your notes private.'
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Privacy section ── */}
      <section style={{ maxWidth: '1100px', margin: '0 auto', padding: 'clamp(48px, 6vw, 72px) 24px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '48px',
            alignItems: 'center',
          }}
        >
          <div>
            <div
              style={{
                color: 'var(--v2-primary)',
                fontWeight: 700,
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: '12px',
              }}
            >
              {t('featuresPage.privacy.label', { defaultValue: 'PRIVACY' })}
            </div>
            <h2
              style={{
                fontSize: 'clamp(22px, 2.8vw, 34px)',
                fontWeight: 800,
                color: 'var(--v2-ink)',
                marginBottom: '12px',
                lineHeight: 1.2,
              }}
            >
              {t('featuresPage.privacy.sectionTitle', 'Privacy built in, not bolted on')}
            </h2>
            <p style={{ fontSize: '15px', color: 'var(--v2-ink-muted)', lineHeight: 1.65 }}>
              {t('featuresPage.privacy.sectionSubtitle', 'Your data stays yours. Always.')}
            </p>
          </div>

          <div
            style={{
              background: 'var(--v2-card)',
              border: '1px solid var(--v2-line)',
              borderRadius: '20px',
              padding: '28px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {privacyRowDefs.map((row) => (
              <div
                key={row.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '12px',
                  borderRadius: '12px',
                  background: 'var(--v2-bg-subtle)',
                  border: '1px solid var(--v2-line)',
                }}
              >
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: row.bg,
                    color: row.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {row.icon}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--v2-ink)' }}>
                    {t(
                      `featuresPage.privacy.rows.${row.key}.title`,
                      row.key === 'onDevice'
                        ? 'On-device by default'
                        : row.key === 'encrypted'
                        ? 'End-to-end encrypted sync'
                        : row.key === 'delete'
                        ? 'Delete means delete'
                        : 'Zero third-party sharing'
                    )}
                  </div>
                  <div style={{ fontSize: '12.5px', color: 'var(--v2-ink-muted)', marginTop: '2px' }}>
                    {t(
                      `featuresPage.privacy.rows.${row.key}.sub`,
                      row.key === 'onDevice'
                        ? 'Audio never leaves your phone'
                        : row.key === 'encrypted'
                        ? "We see ciphertext. That's it."
                        : row.key === 'delete'
                        ? 'No shadow copies on our servers'
                        : "We're the product, not you"
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        style={{
          background: 'var(--v2-bg)',
          padding: 'clamp(0px, 2vw, 16px) 24px clamp(64px, 8vw, 96px)',
        }}
      >
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div
            style={{
              borderRadius: '24px',
              background: 'linear-gradient(135deg, var(--v2-primary), var(--v2-violet-400))',
              padding: 'clamp(48px, 6vw, 80px) clamp(24px, 5vw, 64px)',
              textAlign: 'center',
            }}
          >
            <h2
              style={{
                fontSize: 'clamp(28px, 4vw, 48px)',
                fontWeight: 800,
                color: '#fff',
                marginBottom: '12px',
                lineHeight: 1.15,
              }}
            >
              {t('featuresPage.cta.title', 'Start capturing everything')}
            </h2>
            <p
              style={{
                fontSize: '18px',
                color: 'rgba(255,255,255,0.8)',
                marginBottom: '32px',
                maxWidth: '480px',
                margin: '0 auto 32px',
              }}
            >
              {t('featuresPage.cta.subtitle', 'Free to download. No credit card required.')}
            </p>
            <a
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                background: '#fff',
                color: 'var(--v2-primary)',
                padding: '15px 36px',
                borderRadius: '999px',
                fontWeight: 700,
                fontSize: '16px',
                textDecoration: 'none',
                boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                transition: 'opacity 0.15s',
              }}
            >
              {t('featuresPage.cta.button', 'Download Notely AI →')}
            </a>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '13px', marginTop: '14px' }}>
              {t('featuresPage.cta.note', 'Available on iOS, Android & macOS')}
            </p>
          </div>
        </div>
      </section>

    </main>
  );
};

export default FeaturesPage;
